<template>
  <svg style="display: none;">
    <g :id="trackerId" class="tracker" style="fill:#484b60; cursor: pointer;">
      <image xlink:href="/img/shadow.min.png" x="-43" y="-33" height="380px" width="340px"></image>
      <polygon class="frame" :fill="fillTrackerColor" stroke="#000000" stroke-width="4" points="50,30 50,200 200,280 200,110 50,200 200,110"/>
      <polygon class="panel1" stroke-width="2.5" stroke="#000000" points="0,0 0,60 33,60 33,0 33,60 66,60 66,0 66,60 99,60 99,0" />
      <polygon class="panel2" stroke-width="2.5" stroke="#000000" points="150,80 150,140 183,140 183,80 183,140 216,140 216,80 216,140 249,140 249,80" />
      <polygon class="panel3" stroke-width="2.5" stroke="#000000" points="150,250 150,310 183,310 183,250 183,310 216,310 216,250 216,310 249,310 249,250" />
      <polygon class="panel4" stroke-width="2.5" stroke="#000000" points="0,170 0,230 33,230 33,170 33,230 66,230 66,170 66,230 99,230 99,170" />
      <foreignObject>
        <div v-if="ready" xmlns="http://www.w3.org/1999/xhtml"><slot></slot></div>
      </foreignObject>
    </g>
  </svg>
</template>

<script>
import { findRealParent } from 'vue2-leaflet';
import './L.CustomSVG';
import { computeTrackerBounds, shadeBlendConvert, armErrors } from './helpers';

export default {
  name: 'LTracker',
  props: {
    tracker: {
      type: Object,
      required: true
    },
    highlighted: {
      type: Boolean,
      required: true
    },
    selected: {
      type: Boolean,
      required: true
    },
    trackerStatus: {
      type: Object,
      required: true
    },
    angle: {
      type: Number,
      default: 0
    },
    infoActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      trackerId: `t_${this.tracker.gcu_code}_${this.tracker.lcu_code}`,
      ready: false
    };
  },
  computed: {
    fillTrackerColor() {
      if (this.trackerStatus && this.trackerStatus.status) {
        if (this.trackerStatus.lastStatusCollection === -1 || this.trackerStatus.lastStatusCollection > 600) return '#777777';

        switch (this.trackerStatus.status.code) {
          case 3:
            return '#e1f7d5';
          case 4:
            return '#f0ad4e';
          case 8:
            return '#ccc9c9';
          case 7:
            return '#337ab7';
          case 13:
            return '#ff0000';
          default:
            return '#7F5839';
        }
      } else {
        return 'none';
      }
    },
    viewState() {
      if (this.highlighted) return 0;
      else if (this.selected) return 1;
      else return 2;
    }
  },
  methods: {
    updatePanelView() {
      if (!this.mapObject) return;

      const val = this.viewState;

      if (this.trackerStatus && this.trackerStatus.status) {
        if (this.trackerStatus.armErrors.length === 4 && this.trackerStatus.lastStatusCollection <= 600) {
          for (let i = 0; i < 4; i++) {
            let errorCode = this.trackerStatus.armErrors[i].code;
            if (!armErrors[errorCode]) errorCode = 'other';

            if (val === 0) this.mapObject.setSVGElementStyle(`panel${i + 1}`, `fill: ${shadeBlendConvert(0.2, `#${armErrors[errorCode]}`, '#000000')}`);
            else if (val === 1) this.mapObject.setSVGElementStyle(`panel${i + 1}`, `fill: ${shadeBlendConvert(0.5, `#${armErrors[errorCode]}`, '#000000')}`);
            else this.mapObject.setSVGElementStyle(`panel${i + 1}`, `fill: #${armErrors[errorCode]}`);
          }
        } else {
          for (let i = 0; i < 4; i++) {
            if (val === 0) this.mapObject.setSVGElementStyle(`panel${i + 1}`, `fill: ${shadeBlendConvert(0.2, '#aaaaaa', '#000000')}`);
            else if (val === 1) this.mapObject.setSVGElementStyle(`panel${i + 1}`, `fill: ${shadeBlendConvert(0.5, '#aaaaaa', '#000000')}`);
            else this.mapObject.setSVGElementStyle(`panel${i + 1}`, 'fill: #aaaaaa');
          }
        }
      } else {
        for (let i = 0; i < 4; i++) {
          if (val === 0) this.mapObject.setSVGElementStyle(`panel${i + 1}`, 'fill: #FF9E1B');
          else if (val === 1) this.mapObject.setSVGElementStyle(`panel${i + 1}`, 'fill: #FFFF00');
          else this.mapObject.setSVGElementStyle(`panel${i + 1}`, 'fill: #484b60');
        }
      }
    },
    unbindTooltip () {
      const tooltip = this.mapObject ? this.mapObject.getTooltip() : null;
      if (tooltip) {
        this.mapObject.unbindTooltip();
      }
    },
    unbindPopup () {
      const popup = this.mapObject ? this.mapObject.getPopup() : null;
      if (popup) {
        this.mapObject.unbindPopup();
      }
    }
  },
  mounted () {
    const gEl = this.$el.children[0];
    gEl.remove();
    this.$el.remove();

    this.mapObject = L.customSVG(gEl, computeTrackerBounds({ lat: this.tracker.coords.y, lng: this.tracker.coords.x }), { width: 290, height: 330 }, { angle: this.angle });

    // Set up events for mobile and pc devices
    if (this.$feature.mobile) {
      this.mapObject.on('click', e => {
        if (!this.infoActive) this.$emit('click', e, this.tracker);
      });
      this.mapObject.on('contextmenu', e => {
        this.$emit('contextmenu', e);
      });
    } else {
      this.mapObject.on('click', e => this.$emit('click', e));
      this.mapObject.on('mouseover', e => this.$emit('mouseover'));
      this.mapObject.on('mouseout', e => this.$emit('mouseout'));
      this.mapObject.on('contextmenu', e => {
        if (!this.infoActive) this.$emit('contextmenu', e);
      });
    }

    this.updatePanelView();

    this.parentContainer = findRealParent(this.$parent);
    this.parentContainer.addLayer(this, false);
    this.ready = true;
  },
  beforeDestroy () {
    this.parentContainer.removeLayer(this);
  },
  watch: {
    viewState() {
      this.updatePanelView();
    },
    trackerStatus() {
      this.updatePanelView();
    }
  }
};
</script>

<style>
.leaflet-overlay-pane path,
.leaflet-marker-icon,
.leaflet-image-layer,
.tracker {
	cursor: pointer
}

.tracker {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
